import { useCallback, useRef, useState } from 'react'

// gap-1 between chain tabs, in px — used by overflow measurement.
const CHAIN_TAB_GAP_PX = 4

// Measures how many chain tabs fit in the row (no scroll) and reports the
// overflow to the parent. Mirrors useScrollEdgeFade's callback-ref +
// ResizeObserver pattern to avoid useEffect.
export const useChainTabsOverflow = ({
  chainIds,
  onOverflowChange,
}: {
  chainIds: number[]
  onOverflowChange?: (chainIds: number[]) => void
}) => {
  const [visibleCount, setVisibleCount] = useState(chainIds.length)
  const areaRef = useRef<HTMLDivElement | null>(null)
  const measureRef = useRef<HTMLDivElement | null>(null)
  const lastReportedRef = useRef<string | null>(null)

  const remeasure = useCallback(() => {
    const area = areaRef.current
    const measureRow = measureRef.current
    if (!area || !measureRow) return

    const available = area.clientWidth
    const widths = Array.from(measureRow.children).map(
      (child) => (child as HTMLElement).getBoundingClientRect().width,
    )
    // Monotonic cumulative widths → count of prefix tabs that fit.
    const cumulative = widths.reduce<number[]>((acc, width, index) => {
      const prev = index === 0 ? 0 : acc[index - 1] + CHAIN_TAB_GAP_PX
      acc.push(prev + width)
      return acc
    }, [])
    const count = cumulative.filter((total) => total <= available).length

    setVisibleCount((prev) => (prev === count ? prev : count))

    const overflow = chainIds.slice(count)
    const key = overflow.join(',')
    if (lastReportedRef.current !== key) {
      lastReportedRef.current = key
      onOverflowChange?.(overflow)
    }
  }, [chainIds, onOverflowChange])

  const observe = useCallback(
    (
      el: HTMLDivElement | null,
      assign: (node: HTMLDivElement | null) => void,
    ) => {
      assign(el)
      if (!el) return
      remeasure()
      const observer = new ResizeObserver(remeasure)
      observer.observe(el)
      return () => observer.disconnect()
    },
    [remeasure],
  )

  const areaCallbackRef = useCallback(
    (el: HTMLDivElement | null) =>
      observe(el, (node) => (areaRef.current = node)),
    [observe],
  )
  const measureCallbackRef = useCallback(
    (el: HTMLDivElement | null) =>
      observe(el, (node) => (measureRef.current = node)),
    [observe],
  )

  return { visibleCount, areaCallbackRef, measureCallbackRef }
}
